import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabase';
import { authHeaders } from '../../utils/api';
import MessageContent from '../messages/MessageContent';
import AttachmentBubble from '../messages/AttachmentBubble';

// ─── Messages Page ────────────────────────────────────────────────────────────
function MessagesPage({ conversations, setConversations, activeConvoId, setActiveConvoId, bookingRequests, setBookingRequests, authUser, vendorProfile, userEvents, loadMessages, setTab }) {
  const [draft, setDraft] = useState('');
  // Determine display name: vendor name for vendors, org/contact name for hosts
  const hostDisplayName = (() => {
    if (userEvents && userEvents.length > 0) {
      const e = userEvents[0];
      return e.org_name || e.contact_name || null;
    }
    return authUser?.user_metadata?.full_name || null;
  })();
  const senderName = vendorProfile?.contact_name || vendorProfile?.name || hostDisplayName || authUser?.email || 'User';
  const [uploading, setUploading] = useState(false);
  const [stagedAttachments, setStagedAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const activeConvo = conversations.find(c => c.id === activeConvoId);
  const isVendorInConvo = !!vendorProfile && activeConvo?.vendorId === authUser?.id;

  // Scroll to bottom of messages container (not the page)
  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeConvo?.messages?.length]);

  // Mark messages as read when conversation is opened
  useEffect(() => {
    if (!activeConvoId || !authUser) return;
    supabase.from('messages').update({ is_read: true })
      .eq('conversation_id', activeConvoId)
      .eq('recipient_id', authUser.id)
      .eq('is_read', false)
      .then(() => { if (loadMessages) loadMessages(); })
      .catch(err => console.error('Mark messages as read failed:', err));
  }, [activeConvoId, authUser, loadMessages]);

  const addMessage = async (text, attachments) => {
    const uid = authUser?.id || 'anon';
    const conv = conversations.find(c => c.id === activeConvoId);
    if (!conv) return;
    // Determine role from conversation context: you're the vendor only if
    // your user ID matches the conversation's vendorId. This prevents
    // admin/host users who also have vendor profiles from being misidentified.
    const isVendor = !!vendorProfile && conv.vendorId === authUser?.id;
    // If this is the first message in a conversation opened via "Message Host", initialize it now
    if (conv.status === 'pending_first_message') {
      const sysText = `Conversation started by ${vendorProfile?.name || 'a vendor'} about ${conv.eventName || 'an event'}. Contact info is shared only after a booking is confirmed.`;
      await supabase.from('messages').insert({ conversation_id: activeConvoId, sender_id: 'system', sender_type: 'system', recipient_id: uid, recipient_type: 'vendor', event_name: conv.eventName || '', message_text: sysText, is_read: true });
      const hostId = conv.hostId || (() => { const parts = activeConvoId.split('_'); return parts[0] === uid ? parts[1] : parts[0]; })();
      await supabase.from('messages').insert({ conversation_id: activeConvoId, sender_id: 'system', sender_type: 'system', recipient_id: hostId, recipient_type: 'host', event_name: conv.eventName || '', message_text: sysText, is_read: false });
      setConversations(convos => convos.map(c => c.id === activeConvoId ? { ...c, status: 'active', messages: [{ id: Date.now()-1, from: 'system', text: sysText, ts: new Date().toISOString() }] } : c));
    }
    // Determine recipient from canonical convoId: the other party's ID
    const convoIdParts = activeConvoId.split('_');
    const otherId = convoIdParts[0] === uid ? convoIdParts[1] : convoIdParts[0];
    const recipientId = otherId || conv.vendorId || 'unknown';
    const ts = new Date().toISOString();
    // Update UI immediately
    const myFrom = isVendor ? 'vendor' : 'host';
    setConversations(convos => convos.map(c => {
      if (c.id !== activeConvoId) return c;
      return { ...c, messages: [...c.messages, { id: Date.now(), from: myFrom, senderName, text: text || '', ts, ...(attachments && attachments.length > 0 ? { attachments } : {}) }] };
    }));
    // Save to Supabase — try with attachments, fall back without
    const msgPayload = {
      conversation_id: activeConvoId,
      sender_id: uid, sender_type: isVendor ? 'vendor' : 'host',
      recipient_id: recipientId, recipient_type: isVendor ? 'host' : 'vendor',
      event_name: conv.eventName || '',
      message_text: text || '(file attachment)',
      is_read: false,
    };
    let saved = false;
    if (attachments && attachments.length > 0) {
      const { error } = await supabase.from('messages').insert({ ...msgPayload, attachments });
      if (error) {
        console.error('Message with attachments failed, retrying without:', error.message);
        const { error: e2 } = await supabase.from('messages').insert(msgPayload);
        saved = !e2;
        if (e2) console.error('Message save failed:', e2.message);
      } else { saved = true; }
    } else {
      const { error } = await supabase.from('messages').insert(msgPayload);
      saved = !error;
      if (error) console.error('Message save failed:', error.message);
    }
    // Send email notification to recipient — always try regardless of save status
    (async () => {
      try {
        let recipientEmail = null, recipientName = null, recipientType = 'vendor';
        // Try vendors table first (most common: host/admin messaging a vendor)
        const { data: vendorRow } = await supabase.from('vendors').select('contact_email,contact_name').eq('user_id', recipientId).limit(1);
        if (vendorRow?.[0]) {
          recipientEmail = vendorRow[0].contact_email;
          recipientName = vendorRow[0].contact_name;
          recipientType = 'vendor';
        } else {
          // Fallback: vendor messaging a host — look up in events table
          const { data: eventRow } = await supabase.from('events').select('contact_email,contact_name').eq('user_id', recipientId).limit(1);
          if (eventRow?.[0]) {
            recipientEmail = eventRow[0].contact_email;
            recipientName = eventRow[0].contact_name;
            recipientType = 'host';
          }
        }
        // Never send notification to yourself
        if (recipientEmail && recipientEmail.toLowerCase() === authUser?.email?.toLowerCase()) {
          console.warn('Skipping message notification — recipient is the sender');
          return;
        }
        if (recipientEmail) {
          authHeaders().then(h => fetch('/api/send-message-notification', { method:'POST', headers: h,
            body: JSON.stringify({ recipientEmail, recipientName, senderName, senderType: isVendor ? 'vendor' : 'host', recipientType, eventName: conv.eventName || '', messagePreview: text || '' }),
          })).catch(e=>console.error('Message notification failed:',e));
        }
      } catch (e) { console.error('Email lookup error:', e); }
    })();
  };

  const sendMessage = () => {
    if (!draft.trim() && stagedAttachments.length === 0) return;
    addMessage(draft.trim() || (stagedAttachments.length > 0 ? '' : ''), stagedAttachments.length > 0 ? stagedAttachments : undefined);
    setDraft('');
    setStagedAttachments([]);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const maxSize = 10 * 1024 * 1024; // 10MB
    const oversized = files.find(f => f.size > maxSize);
    if (oversized) { alert(`"${oversized.name}" is too large. Maximum file size is 10MB.`); return; }
    setUploading(true);
    const bucket = 'vendor-files';
    const convoId = activeConvoId;
    const uploaded = [];
    for (const file of files) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100);
      const path = `messages/${convoId}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage.from(bucket).upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) { console.error('Upload error:', upErr); continue; }
      const url = supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
      uploaded.push({ name: file.name, url, type: file.type, size: file.size });
    }
    if (uploaded.length > 0) {
      setStagedAttachments(prev => [...prev, ...uploaded]);
    } else {
      alert('File upload failed. Please try again.');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fmtTs = ts => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const respondToBooking = async (reqId, status, vendorMsg='') => {
    const respondedAt = new Date().toISOString();
    // Persist status change to Supabase first
    const { error } = await supabase.from('booking_requests')
      .update({ status, vendor_message: vendorMsg, responded_at: respondedAt })
      .eq('id', reqId);
    if (error) {
      console.error('Failed to update booking request status:', error);
      alert('Failed to update booking status. Please try again.');
      return;
    }
    // Only update UI after DB confirms
    setBookingRequests(reqs => reqs.map(r =>
      r.id === reqId ? {...r, status, vendorMessage: vendorMsg, respondedAt} : r
    ));
    // Add system message to conversation thread
    const req = bookingRequests.find(r => r.id === reqId);
    if (req) {
      const uid = authUser?.id || 'anon';
      const convoId = conversations.find(c => c.vendorId === req.vendorId)?.id || `${uid}_${req.vendorId}`;
      const msg = status === 'accepted'
        ? `✅ Booking accepted! ${vendorMsg ? 'Vendor note: ' + vendorMsg : ''} The host will be in touch to confirm details. You're all set for ${req.eventName || 'the event'}!`
        : `❌ Booking declined. ${vendorMsg ? 'Reason: ' + vendorMsg : 'The vendor is unavailable for this date.'} We recommend messaging other vendors.`;
      supabase.from('messages').insert({
        conversation_id: convoId, sender_id: 'system', sender_type: 'system',
        recipient_id: uid, recipient_type: 'host',
        event_name: req.eventName || '', message_text: msg, is_read: false,
      }).catch(e=>console.error('API call failed:',e));
      setConversations(convos => convos.map(c => {
        if (c.vendorId !== req.vendorId) return c;
        return {...c, status: status==='accepted'?'booked':'active', messages: [...c.messages, {id:Date.now(), from:'system', text: msg, ts:new Date().toISOString()}]};
      }));
      // Email host about booking response
      if (req.hostEmail) {
        authHeaders().then(h => fetch('/api/send-booking-response', { method:'POST', headers: h,
          body: JSON.stringify({ hostEmail:req.hostEmail, hostName:req.hostName, vendorName:req.vendorName, vendorCategory:req.vendorCategory, eventName:req.eventName, eventDate:req.eventDate, status, vendorMessage:vendorMsg }),
        })).catch(e=>console.error('API call failed:',e));
      }
    }
  };

  const pendingRequests = (bookingRequests||[]).filter(r => r.status === 'pending');

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 64px)', background:'#f5f0ea', overflow:'hidden' }}>

      <div className="msg-layout" style={{ display:'flex', flex:1, overflow:'hidden' }}>

      {/* Sidebar */}
      <div className="msg-sidebar" style={{ width:280, minWidth:220, background:'#1a1410', display:'flex', flexDirection:'column', borderRight:'1px solid #2d2118', flexShrink:0 }}>
        <div style={{ padding:'20px 16px 12px', borderBottom:'1px solid #2d2118' }}>
          <div style={{ fontFamily:'Playfair Display,serif', fontSize:18, color:'#e8c97a', marginBottom:4 }}>Messages</div>
          <div style={{ fontSize:12, color:'#7a6a5a' }}>{conversations.length} conversation{conversations.length!==1?'s':''}</div>
        </div>
        <div className="msg-sidebar-list" style={{ flex:1, overflowY:'auto' }}>
          {conversations.length === 0 && (
            <div style={{ padding:24, color:'#7a6a5a', fontSize:13, textAlign:'center', lineHeight:1.6 }}>
              No conversations yet.<br/>Browse vendors and click<br/>"Message Vendor" to start.
            </div>
          )}
          {conversations.map(c => {
            const amVendorHere = !!vendorProfile && c.vendorId === authUser?.id;
            return (
            <div key={c.id}
              onClick={() => setActiveConvoId(c.id)}
              style={{ padding:'14px 16px', cursor:'pointer', borderBottom:'1px solid #2d2118',
                background: c.id === activeConvoId ? '#2d2118' : 'transparent',
                transition:'background 0.15s' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                <span style={{ fontSize:22 }}>{amVendorHere ? '👤' : c.vendorEmoji}</span>
                <div>
                  <div style={{ fontSize:14, fontWeight:600, color:'#fff' }}>{amVendorHere ? (c.hostName || 'Host') : c.vendorName}</div>
                  <div style={{ fontSize:11, color:'#7a6a5a', textTransform:'uppercase', letterSpacing:1 }}>{amVendorHere ? 'Host' : c.vendorCategory}</div>
                </div>
              </div>
              {c.eventName && <div style={{ fontSize:11, color:'#c8a850', marginBottom:2, fontWeight:600 }}>📋 {c.eventName}</div>}
              <div style={{ fontSize:12, color:'#a89a8a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {(c.messages || []).at(-1)?.text?.slice(0, 50)}
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Chat area */}
      {!activeConvo ? (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12, color:'#a89a8a' }}>
          <div style={{ fontSize:48 }}>💬</div>
          <div style={{ fontFamily:'Playfair Display,serif', fontSize:22, color:'#1a1410' }}>Select a conversation</div>
          <div style={{ fontSize:14 }}>Choose a vendor from the left to view your messages</div>
        </div>
      ) : (
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>

          {/* Chat header */}
          <div style={{ background:'#fff', borderBottom:'1px solid #e8ddd0', padding:'14px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:28 }}>{activeConvo.vendorEmoji}</span>
              <div>
                <button onClick={()=>{if(setTab){setTab(vendorProfile ? 'vendor-dashboard' : 'matches');window.scrollTo({top:0});}}} style={{background:'none',border:'none',padding:0,cursor:'pointer',fontFamily:'Playfair Display,serif',fontSize:18,color:'#1a1410',textDecoration:'underline',textDecorationColor:'#e8ddd0',textAlign:'left'}}>
                  {isVendorInConvo ? (activeConvo.hostName || 'Host') : activeConvo.vendorName}
                </button>
                {!isVendorInConvo && <div style={{ fontSize:12, color:'#a89a8a', textTransform:'uppercase', letterSpacing:1 }}>{activeConvo.vendorCategory}</div>}
                {isVendorInConvo && <div style={{ fontSize:12, color:'#a89a8a' }}>You ({activeConvo.vendorName})</div>}
                {activeConvo.eventName && <button onClick={()=>{if(setTab){
                  const isVendorUser = !!vendorProfile;
                  if (!isVendorUser) { setTab('host-dashboard'); }
                  else {
                    // Check if vendor is booked for this event
                    const bookedForEvent = (bookingRequests||[]).some(r=>r.status==='accepted'&&activeConvo.eventName.includes(r.eventName||r.event_name));
                    setTab(bookedForEvent ? 'vendor-dashboard' : 'opportunities');
                  }
                  window.scrollTo({top:0});
                }}} style={{background:'none',border:'none',padding:0,cursor:'pointer',fontSize:12,color:'#c8a850',fontWeight:600,marginTop:2,textAlign:'left'}}>📋 {activeConvo.eventName} →</button>}
              </div>
            </div>
            <div style={{display:'flex',gap:6}}>
              <button onClick={async()=>{if(!window.confirm('Delete this conversation permanently? This cannot be undone.'))return;const cid=activeConvoId;const uid=authUser?.id;const{error}=await supabase.from('messages').delete().eq('conversation_id',cid);if(error){console.error('Delete failed:',error.message);const{error:e2}=await supabase.from('messages').delete().eq('conversation_id',cid).or(`sender_id.eq.${uid},recipient_id.eq.${uid}`);if(e2)console.error('Filtered delete also failed:',e2.message);}setConversations(cs=>cs.filter(c=>c.id!==cid));setActiveConvoId(null);if(loadMessages)setTimeout(loadMessages,300);}} style={{background:'#fdecea',color:'#8b1a1a',border:'1px solid #f5c6c6',borderRadius:6,padding:'5px 12px',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Delete</button>
            </div>
          </div>

          {/* Platform protection notice */}
          <div style={{ background:'#fdf9f5', borderBottom:'1px solid #e8ddd0', padding:'7px 24px', fontSize:12, color:'#7a6a5a', display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
            🔒 <strong>Secure platform messaging.</strong> Contact info is shared only after a booking is confirmed. All interactions are covered by our <button onClick={()=>{if(setTab){setTab('tos');window.scrollTo({top:0});}}} style={{background:'none',border:'none',padding:0,color:'#c8a84b',textDecoration:'underline',fontWeight:600,cursor:'pointer',fontSize:12,fontFamily:'inherit'}}>Terms of Service</button>.
          </div>

          {/* Messages */}
          <div style={{ flex:1, overflowY:'auto', padding:24, display:'flex', flexDirection:'column', gap:12 }}>
            {activeConvo.messages.map(msg => (
              <div key={msg.id} style={{
                display:'flex',
                flexDirection: msg.from==='system' ? 'column' : msg.from==='host' ? 'flex-end' : 'flex-start',
                alignItems: msg.from==='system' ? 'center' : undefined
              }}>
                {msg.from === 'system' && (
                  <div style={{ background:'#f5f0ea', border:'1px solid #e8ddd0', borderRadius:8, padding:'8px 16px', fontSize:12, color:'#7a6a5a', maxWidth:480, textAlign:'center' }}>
                    {msg.text}
                  </div>
                )}
                {msg.from !== 'system' && (
                  <div style={{ maxWidth:'70%' }}>
                    <div style={{ fontSize:11, color:'#a89a8a', marginBottom:3, textAlign: msg.from==='host' ? 'right' : 'left' }}>
                      {msg.senderName || (msg.from==='vendor' ? activeConvo.vendorName : 'You')} · {fmtTs(msg.ts)}
                    </div>
                    <div style={{
                      background: msg.from==='host' ? '#1a1410' : '#fff',
                      color: msg.from==='host' ? '#e8c97a' : '#1a1410',
                      border: '1px solid ' + (msg.from==='host' ? '#1a1410' : '#e8ddd0'),
                      borderRadius: msg.from==='host' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                      padding:'10px 14px', fontSize:14, lineHeight:1.6
                    }}>
                      <MessageContent text={msg.text} isHost={msg.from==='host'} />
                    </div>
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:6 }}>
                        {msg.attachments.map((att, i) => (
                          <AttachmentBubble key={i} att={att} isHost={msg.from==='host'} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div style={{ background:'#fff', borderTop:'1px solid #e8ddd0', padding:16, flexShrink:0 }}>
            {/* Staged attachments preview */}
            {stagedAttachments.length > 0 && (
              <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:10,padding:'8px 10px',background:'#fdf9f5',borderRadius:8,border:'1px solid #e8ddd0'}}>
                {stagedAttachments.map((att,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:6,background:'#fff',border:'1px solid #e0d5c5',borderRadius:6,padding:'4px 8px',fontSize:12}}>
                    {att.type?.startsWith('image/') ? <img src={att.url} alt={att.name} style={{width:32,height:32,objectFit:'cover',borderRadius:4}} /> : <span>📎</span>}
                    <span style={{maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{att.name}</span>
                    <button onClick={()=>setStagedAttachments(prev=>prev.filter((_,j)=>j!==i))} style={{background:'none',border:'none',color:'#c0392b',cursor:'pointer',fontSize:14,padding:0,lineHeight:1}}>×</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={()=>fileInputRef.current?.click()} disabled={uploading}
                title="Share a file or document"
                style={{ background:'#f5f0ea', color:'#7a6a5a', border:'1px solid #e0d5c5', borderRadius:8, padding:'0 12px', fontSize:18, cursor:'pointer', flexShrink:0, opacity:uploading?0.5:1 }}>
                {uploading ? '⏳' : '📎'}
              </button>
              <input ref={fileInputRef} type="file" multiple onChange={handleFileUpload}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                style={{ display:'none' }} />
              <textarea value={draft} onChange={e=>setDraft(e.target.value)}
                onKeyDown={e=>{ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); sendMessage(); }}}
                placeholder="Type a message or share a link... (Enter to send)"
                rows={2}
                style={{ flex:1, border:'1.5px solid #e0d5c5', borderRadius:8, padding:'10px 14px', fontSize:14, fontFamily:'DM Sans,sans-serif', resize:'none', outline:'none', background:'#fdf9f5' }} />
              <button onClick={sendMessage}
                style={{ background:'#1a1410', color:'#e8c97a', border:'none', borderRadius:8, padding:'0 20px', fontSize:20, cursor:'pointer', flexShrink:0 }}>
                ➤
              </button>
            </div>
            <div style={{ fontSize:11, color:'#a89a8a', marginTop:6, lineHeight:1.5 }}>
              📎 Share documents, images, or links<br/>This conversation is protected under the <button onClick={()=>{if(setTab){setTab('tos');window.scrollTo({top:0});}}} style={{background:'none',border:'none',padding:0,color:'#c8a84b',fontWeight:700,cursor:'pointer',fontSize:11,fontFamily:'inherit',textDecoration:'underline'}}>South Jersey Vendor Market Non-Circumvention Agreement</button>. Vendor contact info is shared only after an accepted booking on a posted event.
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default MessagesPage;
